import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  Navigate,
} from "react-router-dom";
import {
  getAuth,
  onAuthStateChanged,
  signOut,
  signInWithPopup,
  GoogleAuthProvider,
} from "firebase/auth";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { db } from "./firebase";
import Home from "./components/Home";
import Agenda from "./components/Agenda";
import Classes from "./components/Classes";
import ClassDetail from "./components/ClassDetail";
import AdminLogin from "./components/AdminLogin";
import AdminDashboard from "./components/AdminDashboard";
import ClassesDashboard from "./components/ClassesDashboard";
import UserLogin from "./components/UserLogin";
import UserProfile from "./components/UserProfile";
import EventRedirect from "./components/EventRedirect";
import AdminUserActivity from "./components/AdminUserActivity";
import AdminManagement from "./components/AdminManagement"; // Add this import
import "./App.css";

function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false); // Add this line
  const [siteSettings, setSiteSettings] = useState({
    showEvents: true,
    showClasses: true,
  });

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);

      if (currentUser) {
        await checkAdminStatus(currentUser);
      } else {
        setIsAdmin(false);
        setIsSuperAdmin(false);
      }

      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const checkAdminStatus = async (currentUser) => {
    // Check if super admin (hardcoded emails)
    const superAdminEmails = ["joaquinlen@gmail.com", "esteban@owlstudio.tv"];
    const isSuper = superAdminEmails.includes(currentUser.email);
    setIsSuperAdmin(isSuper);

    // Check if regular admin in Firestore
    if (!isSuper) {
      try {
        const adminsRef = collection(db, "admins");
        const q = query(adminsRef, where("userId", "==", currentUser.uid));
        const querySnapshot = await getDocs(q);

        const isRegularAdmin = !querySnapshot.empty;
        setIsAdmin(isRegularAdmin || isSuper);
      } catch (error) {
        console.error("Error checking admin status:", error);
        setIsAdmin(false);
      }
    } else {
      setIsAdmin(true);
    }
  };

  useEffect(() => {
    // Fetch site settings when component mounts
    fetchSiteSettings();
  }, []);

  const fetchSiteSettings = async () => {
    try {
      const settingsRef = doc(db, "siteSettings", "global");
      const settingsSnap = await getDoc(settingsRef);

      if (settingsSnap.exists()) {
        const data = settingsSnap.data();
        setSiteSettings({
          showEvents: data.showEvents !== false, // Default to true if not set
          showClasses: data.showClasses !== false,
        });
      } else {
        // Use defaults if settings don't exist
        setSiteSettings({
          showEvents: true,
          showClasses: true,
        });
      }
    } catch (err) {
      console.error("Error fetching site settings:", err);
      // Use defaults on error
      setSiteSettings({
        showEvents: true,
        showClasses: true,
      });
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const auth = getAuth();
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error("Error logging in with Google:", error);
    }
  };

  const handleLogout = async () => {
    const auth = getAuth();
    await signOut(auth);
  };

  // Protected Route Component for Admin
  const AdminProtectedRoute = ({ children, superAdminOnly = false }) => {
    if (authLoading) {
      return <div className="auth-loading">Checking authentication...</div>;
    }

    if (!user || !isAdmin) {
      return <Navigate to="/admin" />;
    }

    if (superAdminOnly && !isSuperAdmin) {
      return <Navigate to="/" />;
    }

    return children;
  };

  // Protected Route Component for Regular Users
  const UserProtectedRoute = ({ children }) => {
    if (authLoading) {
      return <div className="auth-loading">Checking authentication...</div>;
    }

    if (!user) {
      return <Navigate to="/login" />;
    }

    return children;
  };

  return (
    <Router>
      <div className="App">
        {/* Navigation */}
        <nav className="simple-nav">
          <div className="nav-content">
            <Link to="/" className="logo">
              Paradigmas
            </Link>
            <div className="nav-links">
              <Link to="/" className="nav-link">
                Home
              </Link>

              {/* Only show Agenda link if events are enabled */}
              {siteSettings.showEvents && (
                <Link to="/agenda" className="nav-link">
                  Agenda
                </Link>
              )}

              {/* Only show Classes link if classes are enabled */}
              {siteSettings.showClasses && (
                <Link to="/classes" className="nav-link">
                  Actividades
                </Link>
              )}

              {/* User Authentication Section */}
              {user ? (
                <>
                  <Link to="/profile" className="nav-link">
                    Mi Perfil
                  </Link>
                  {isAdmin && (
                    <>
                      <Link to="/admin/dashboard" className="nav-link">
                        Eventos
                      </Link>
                      <Link to="/admin/classes" className="nav-link">
                        Clases
                      </Link>
                      {isSuperAdmin && (
                        <Link to="/admin/manage" className="nav-link">
                          Administradores
                        </Link>
                      )}
                      <Link to="/admin/activity" className="nav-link">
                        Usuarios
                      </Link>
                    </>
                  )}
                  <button onClick={handleLogout} className="logout-btn">
                    Cerrar Sesión
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="nav-link">
                    Iniciar Sesión
                  </Link>
                </>
              )}
            </div>
          </div>
        </nav>

        {/* Main Content */}
        <main>
          <Routes>
            <Route path="/" element={<Home />} />

            {/* Only create Agenda route if events are enabled */}
            {siteSettings.showEvents && (
              <Route path="/agenda" element={<Agenda />} />
            )}

            {/* Only create Classes routes if classes are enabled */}
            {siteSettings.showClasses && (
              <>
                <Route path="/classes" element={<Classes />} />
                <Route path="/classes/:id" element={<ClassDetail />} />
              </>
            )}

            {/* User Authentication Routes */}
            <Route
              path="/login"
              element={
                user ? (
                  <Navigate to="/" />
                ) : (
                  <UserLogin onGoogleLogin={handleGoogleLogin} />
                )
              }
            />

            <Route
              path="/admin/manage"
              element={
                <AdminProtectedRoute superAdminOnly={true}>
                  <AdminManagement />
                </AdminProtectedRoute>
              }
            />

            <Route
              path="/profile"
              element={
                <UserProtectedRoute>
                  <UserProfile user={user} />
                </UserProtectedRoute>
              }
            />

            {/* Admin Routes - Always accessible for admins regardless of visibility settings */}
            <Route
              path="/admin"
              element={
                user && isAdmin ? (
                  <Navigate to="/admin/dashboard" />
                ) : (
                  <AdminLogin onLogin={() => {}} />
                )
              }
            />

            <Route
              path="/admin/activity"
              element={
                <AdminProtectedRoute>
                  <AdminUserActivity />
                </AdminProtectedRoute>
              }
            />

            <Route
              path="/admin/dashboard"
              element={
                <AdminProtectedRoute>
                  <AdminDashboard />
                </AdminProtectedRoute>
              }
            />

            <Route
              path="/admin/classes"
              element={
                <AdminProtectedRoute>
                  <ClassesDashboard />
                </AdminProtectedRoute>
              }
            />

            <Route path="/event/:eventId" element={<EventRedirect />} />

            {/* Fallback routes for disabled sections */}
            {!siteSettings.showEvents && (
              <Route path="/agenda" element={<Navigate to="/" />} />
            )}

            {!siteSettings.showClasses && (
              <>
                <Route path="/classes" element={<Navigate to="/" />} />
                <Route path="/classes/:id" element={<Navigate to="/" />} />
              </>
            )}

            {/* Optional: 404 page */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </main>

        {/* Simple Footer */}
        <footer className="simple-footer">
          <p>Espacio Paradigmas • 2026</p>
          <p>Charcas y Gallo, CABA</p>
        </footer>
      </div>
    </Router>
  );
}

export default App;
